import { aadConfig, getAccessToken } from '@services/aad';
import apiProxy from '@services/api/apiProxy';
import type { AccountCredits, CreditPack } from '@services/dto/credits';
import axios from 'axios';

/** What the bundle checkout answers: no purchase row exists for a pack, so no purchaseId. */
export type CreditsCheckoutResult = {
  clientSecret?: string;
  amountMinor: number;
  currency?: string;
};

/**
 * The account's credit pool.
 *
 * A read. The balance is derived server-side from the lot ledger rather than
 * stored as a single number, so what this returns is the same arithmetic the
 * debit path uses.
 *
 * ACCOUNT_HOST, not ACCOUNT_API: the credits endpoints are Azure Functions on
 * the fx-act app. The first cut of this file pointed at the act API service,
 * which has no such route — every read 404'd and the page showed its failure
 * state.
 */
export const getAccountCredits = apiProxy(
  'getAccountCredits',
  async (accountId: string, abort?: AbortController): Promise<AccountCredits> => {
    await getAccessToken(aadConfig().accountScopes);

    const { data } = await axios.get<AccountCredits>(
      `${process.env.ACCOUNT_HOST}/v1/accounts/${accountId}/credits`,
      { signal: abort?.signal },
    );
    return data;
  },
);

/**
 * The account's spendable balance, or undefined when it cannot be read.
 *
 * For payment paths: the absence of a readable balance must never block a
 * payment — the caller simply proceeds to the card checkout, and the server
 * makes the authoritative funding decision when the run is created.
 */
export const getCreditsRemaining = async (accountId: string, abort?: AbortController): Promise<number | undefined> => {
  try {
    const credits = await getAccountCredits(accountId, abort);
    return credits?.remaining;
  } catch {
    return undefined;
  }
};

/**
 * The public bundle price list.
 *
 * Deliberately does NOT acquire a token — the endpoint is anonymous so the
 * marketing pricing page can read live bundle prices without a session, same
 * as the report catalog.
 */
export const getCreditBundleCatalog = apiProxy(
  'getCreditBundleCatalog',
  async (abort?: AbortController): Promise<CreditPack[]> => {
    const { data } = await axios.get<CreditPack[]>(`${process.env.ACCOUNT_HOST}/v1/purchases/bundles`, {
      signal: abort?.signal,
    });
    return data;
  },
);

/**
 * Opens a Stripe checkout session for a credit bundle. Answers 409 when the
 * account's subscription already includes reports.
 *
 * Not wrapped in apiProxy: it creates server state, same rule as
 * createPurchaseCheckout.
 */
export const createCreditsCheckout = async (
  accountId: string,
  checkout: { packCode: string; returnUrl: string },
  abort?: AbortController,
): Promise<CreditsCheckoutResult> => {
  await getAccessToken(aadConfig().accountScopes);

  const { data } = await axios.post<CreditsCheckoutResult>(
    `${process.env.ACCOUNT_HOST}/v1/accounts/${accountId}/credits/checkout`,
    checkout,
    { signal: abort?.signal },
  );
  return data;
};
